/**
 * ActorDescriptor Repository - FERP Repository Pattern Implementation
 *
 * Manages ActorDescriptor primitive from the 25-type system with proper:
 * - Tenant isolation using findByTenantAndId pattern
 * - Safety framework integration
 * - Audit trail compliance
 * - Life-critical validation
 * - Complete field parity with backend (33 GraphQL fields)
 */

import { gql } from '@ferp/graphql-client';
import { Actor, ActorType } from '../../types/primitives/Actor';
import { BaseRepository } from './BaseRepository';

// GraphQL fragment with ALL 33 fields from backend
const ACTOR_DESCRIPTOR_FIELDS = gql`
  fragment ActorDescriptorFields on ActorDescriptor {
    # Identity (6 fields)
    id
    tenantId
    name
    version
    actorType
    category

    # Definition & Capabilities (5 fields)
    domain
    description
    capabilities
    requiredPermissions
    configuration

    # Safety & Reliability (8 fields)
    timeoutSeconds
    maxRetries
    backoffSeconds
    backoffMultiplier
    maxBackoffSeconds
    maxConcurrency
    circuitBreakerThreshold
    circuitBreakerTimeoutSeconds

    # Versioning & Lifecycle (6 fields)
    parentId
    active
    deprecated
    deprecationReason
    deprecationDate
    lastModifiedReason

    # Compliance & Regulatory (4 fields)
    regulatoryTags
    criticalityLevel
    requiresDualControl
    retentionYears

    # Audit Trail (5 fields - flattened from AuditInfo)
    createdAt
    updatedAt
    createdBy
    updatedBy
    auditVersion

    # Metadata (2 fields)
    tags
    metadata
  }
`;

/**
 * Map GraphQL ActorDescriptor response to frontend Actor type
 * Handles field name differences and nested structure conversion
 */
function mapGraphQLToActor(gqlActor: any): Actor {
  return ({
    // Identity
    id: gqlActor.id,
    tenantId: gqlActor.tenantId,
    name: gqlActor.name,
    version: gqlActor.version,
    actorType: gqlActor.actorType.toLowerCase() as ActorType, // GraphQL enum is uppercase
    category: gqlActor.category || null,

    // Definition & Capabilities
    domain: gqlActor.domain || null,
    description: gqlActor.description || null,
    capabilities: gqlActor.capabilities || [],
    requiredPermissions: gqlActor.requiredPermissions || [],
    configuration: gqlActor.configuration || {},

    // Safety & Reliability
    timeoutSeconds: gqlActor.timeoutSeconds || null,
    maxRetries: gqlActor.maxRetries || null,
    backoffSeconds: gqlActor.backoffSeconds || null,
    backoffMultiplier: gqlActor.backoffMultiplier || null,
    maxBackoffSeconds: gqlActor.maxBackoffSeconds || null,
    maxConcurrency: gqlActor.maxConcurrency || null,
    circuitBreakerThreshold: gqlActor.circuitBreakerThreshold || null,
    circuitBreakerTimeoutSeconds: gqlActor.circuitBreakerTimeoutSeconds || null,

    // Versioning & Lifecycle
    parentId: gqlActor.parentId || null,
    active: gqlActor.active,
    deprecated: gqlActor.deprecated,
    deprecationReason: gqlActor.deprecationReason || null,
    deprecationDate: gqlActor.deprecationDate || null,
    lastModifiedReason: gqlActor.lastModifiedReason || null,

    // Compliance & Regulatory
    regulatoryTags: gqlActor.regulatoryTags || [],
    criticalityLevel: gqlActor.criticalityLevel || null,
    requiresDualControl: gqlActor.requiresDualControl ?? null,
    retentionYears: gqlActor.retentionYears || null,

    // Audit Trail - flat fields matching Actor type
    createdBy: gqlActor.createdBy,
    createdAt: gqlActor.createdAt,
    updatedBy: gqlActor.updatedBy || gqlActor.createdBy,
    updatedAt: gqlActor.updatedAt,
    auditVersion: gqlActor.auditVersion || 1,

    // Metadata
    tags: gqlActor.tags || [],
    metadata: gqlActor.metadata || {},
  }) as Actor;
}

/**
 * Expert metadata utilities for working with ActorDescriptor.metadata JsonValue
 * Maintains FERP's data-driven architecture by keeping metadata as pure JSON
 */
export class ExpertMetadataUtils {
  /**
   * Extract expert tier from ActorDescriptor metadata (data-driven)
   */
  static getExpertTier(metadata: any): string | null {
    return metadata?.expert_tier || null;
  }

  /**
   * Extract specializations from ActorDescriptor metadata (data-driven)
   */
  static getSpecializations(metadata: any): string[] {
    return metadata?.specializations || [];
  }

  /**
   * Extract current capacity from ActorDescriptor metadata (data-driven)
   */
  static getCurrentCapacity(metadata: any): number {
    return metadata?.current_capacity || 0;
  }

  /**
   * Extract availability status from ActorDescriptor metadata (data-driven)
   */
  static getAvailabilityStatus(metadata: any): string {
    return metadata?.availability_status || 'unavailable';
  }

  /**
   * Extract success rate from ActorDescriptor metadata (data-driven)
   */
  static getSuccessRate(metadata: any): number {
    return metadata?.success_rate || 0;
  }

  /**
   * Check compliance framework validation (data-driven)
   */
  static isComplianceValidated(metadata: any, framework: string): boolean {
    return metadata?.compliance?.[`${framework.toLowerCase()}_validated`] || false;
  }

  /**
   * Extract certifications from ActorDescriptor metadata (data-driven)
   */
  static getCertifications(metadata: any): string[] {
    return metadata?.certifications || [];
  }

  /**
   * Extract hourly rate from ActorDescriptor metadata (data-driven)
   */
  static getHourlyRate(metadata: any): number {
    return metadata?.hourly_rate || 0;
  }
}

// Type aliases for backward compatibility
export type ActorRepository = ActorDescriptorRepository;
export type ExpertTier = string;
export type ExpertMetadata = any;

export class ActorDescriptorRepository extends BaseRepository<Actor> {
  protected entityName = 'ActorDescriptor';

  /**
   * Find actors with filtering (data-driven approach)
   * FERP Pattern: findByTenantAndCriteria
   * Updated: Now fetches ALL 33 fields from backend
   */
  async findActorDescriptorsWithFilter(
    tenantId: string,
    filter: {
      actorType?: string;
      expertTier?: string;
      specializations?: string[];
      availabilityStatus?: string;
      minCapacity?: number;
      maxCapacity?: number;
      complianceFrameworks?: string[];
      active?: boolean;
    }
  ): Promise<Actor[]> {
    await this.validateRepositorySafety({
      operation: 'FIND_ACTORS_WITH_FILTER',
      domain: 'OPERATIONAL',
      tenantId,
      entityType: 'ActorDescriptor',
      criticalityLevel: 'HIGH',
      metadata: { filter }
    });

    const query = gql`
      ${ACTOR_DESCRIPTOR_FIELDS}
      query FindActorDescriptorsWithFilter($tenantId: String!, $filter: ActorDescriptorFilterInput) {
        listActorDescriptorsByType(tenantId: $tenantId, actorType: ${filter.actorType || 'HUMAN'}) {
          items {
            ...ActorDescriptorFields
          }
        }
      }
    `;

    const result = await this.query(query, { tenantId, filter });
    const actors = (result?.items || []).map(mapGraphQLToActor);

    // Log successful query for audit trail
    await this.auditTrail.logOperationSuccess({
      operation: 'FIND_ACTORS_WITH_FILTER',
      tenantId,
      entityType: 'ActorDescriptor',
      resultCount: actors.length,
      criticalityLevel: 'HIGH'
    });

    return actors;
  }

  /**
   * Get available experts matching criteria (data-driven)
   * Updated: Now fetches ALL 33 fields from backend
   */
  async getAvailableExperts(
    tenantId: string,
    criteria: {
      tier?: string;
      specializations?: string[];
      maxCapacity?: number;
      minSuccessRate?: number;
      complianceFrameworks?: string[];
    }
  ): Promise<Actor[]> {
    const query = gql`
      ${ACTOR_DESCRIPTOR_FIELDS}
      query GetAvailableExperts(
        $tenantId: String!,
        $tier: String,
        $specializations: [String!],
        $maxCapacity: Float,
        $minSuccessRate: Float,
        $complianceFrameworks: [String!]
      ) {
        getAvailableExperts(
          tenantId: $tenantId,
          tier: $tier,
          specializations: $specializations,
          maxCapacity: $maxCapacity,
          minSuccessRate: $minSuccessRate,
          complianceFrameworks: $complianceFrameworks
        ) {
          ...ActorDescriptorFields
        }
      }
    `;

    const result = await this.query(query, { tenantId, ...criteria });
    return (result || []).map(mapGraphQLToActor);
  }

  /**
   * Standard FERP findByTenantAndId pattern
   * Updated: Now fetches ALL 33 fields from backend
   */
  async findByTenantAndId(tenantId: string, id: string): Promise<Actor | null> {
    await this.validateRepositorySafety({
      operation: 'FIND_BY_TENANT_AND_ID',
      domain: 'OPERATIONAL',
      tenantId,
      entityType: 'ActorDescriptor',
      criticalityLevel: 'MEDIUM',
      metadata: { id }
    });

    const query = gql`
      ${ACTOR_DESCRIPTOR_FIELDS}
      query GetActorDescriptorByTenantAndId($tenantId: String!, $actorId: ID!) {
        getActorDescriptor(tenantId: $tenantId, actorId: $actorId) {
          ...ActorDescriptorFields
        }
      }
    `;

    const result = await this.query(query, { tenantId, actorId: id });

    await this.auditTrail.logOperationSuccess({
      operation: 'FIND_BY_TENANT_AND_ID',
      tenantId,
      entityType: 'ActorDescriptor',
      found: !!result
    });

    return result ? mapGraphQLToActor(result) : null;
  }

  /**
   * Update expert capacity and availability (data-driven)
   * FERP Pattern: updateByTenantAndId with safety validation
   * Updated: Now fetches ALL 33 fields from backend
   */
  async updateExpertCapacity(
    tenantId: string,
    expertId: string,
    capacity: number,
    status: string
  ): Promise<Actor> {
    await this.validateRepositorySafety({
      operation: 'UPDATE_EXPERT_CAPACITY',
      domain: 'OPERATIONAL',
      tenantId,
      entityType: 'ActorDescriptor',
      criticalityLevel: 'HIGH',
      metadata: { expertId, capacity, status }
    });

    const mutation = gql`
      ${ACTOR_DESCRIPTOR_FIELDS}
      mutation UpdateExpertCapacity($tenantId: String!, $actorId: ID!, $capacity: Int!, $status: String!) {
        updateActorDescriptor(
          tenantId: $tenantId,
          actorId: $actorId,
          input: {
            maxConcurrency: $capacity,
            lastModifiedReason: "Capacity updated to $capacity, status: $status"
          }
        ) {
          ...ActorDescriptorFields
        }
      }
    `;

    const result = await this.mutation(mutation, {
      tenantId,
      actorId: expertId,
      capacity,
      status
    });

    await this.auditTrail.logOperationSuccess({
      operation: 'UPDATE_EXPERT_CAPACITY',
      tenantId,
      entityType: 'ActorDescriptor',
      metadata: { capacity, status }
    });

    return mapGraphQLToActor(result);
  }

  /**
   * Get team capacity metrics using data-driven approach
   */
  async getTeamCapacityMetrics(tenantId: string): Promise<{
    avgCapacity: number;
    availableExperts: number;
    overloadedExperts: number;
    shouldHandoffToSpecialists: boolean;
  }> {
    const query = gql`
      query GetTeamCapacityMetrics($tenantId: String!) {
        getTeamCapacityMetrics(tenantId: $tenantId) {
          avgCapacity
          availableExperts
          overloadedExperts
          shouldHandoffToSpecialists
        }
      }
    `;

    return await this.query(query, { tenantId });
  }

  /**
   * Create actor using data-driven approach
   * Updated: Now returns Actor with ALL 33 fields
   */
  async createActorDescriptor(
    tenantId: string,
    input: {
      name: string;
      actorType: string;
      version?: string;
      category?: string;
      domain?: string;
      description?: string;
      capabilities?: string[];
      requiredPermissions?: string[];
      configuration?: Record<string, unknown>;
      timeoutSeconds?: number;
      maxRetries?: number;
      regulatoryTags?: string[];
      criticalityLevel?: string;
      tags?: string[];
      metadata?: Record<string, unknown>;
    }
  ): Promise<Actor> {
    const mutation = gql`
      ${ACTOR_DESCRIPTOR_FIELDS}
      mutation CreateActorDescriptor($tenantId: String!, $input: GqlCreateActorDescriptorInput!) {
        createActorDescriptor(tenantId: $tenantId, input: $input) {
          ...ActorDescriptorFields
        }
      }
    `;

    const result = await this.mutation(mutation, { tenantId, input });
    return mapGraphQLToActor(result);
  }

  /**
   * Find experts by tier
   * Updated: Returns Actor[] with complete field parity
   */
  async findExpertsByTier(tenantId: string, tier: string): Promise<Actor[]> {
    return await this.findActorDescriptorsWithFilter(tenantId, {
      expertTier: tier,
      active: true
    });
  }

  /**
   * Find experts by specializations
   * Updated: Returns Actor[] with complete field parity
   */
  async findExpertsBySpecializations(tenantId: string, specializations: string[]): Promise<Actor[]> {
    return await this.findActorDescriptorsWithFilter(tenantId, {
      specializations,
      active: true
    });
  }

  /**
   * Assign expert to integration
   * Updated: Now fetches ALL 33 fields from backend
   */
  async assignExpertToIntegration(
    tenantId: string,
    expertId: string,
    integrationId: string
  ): Promise<Actor> {
    const mutation = gql`
      ${ACTOR_DESCRIPTOR_FIELDS}
      mutation AssignExpertToIntegration($tenantId: String!, $expertId: ID!, $integrationId: ID!) {
        assignExpertToIntegration(tenantId: $tenantId, expertId: $expertId, integrationId: $integrationId) {
          ...ActorDescriptorFields
        }
      }
    `;

    const result = await this.mutation(mutation, { tenantId, expertId, integrationId });
    return mapGraphQLToActor(result);
  }

  /**
   * Validate expert for assignment using data-driven approach
   */
  async validateExpertAssignment(
    tenantId: string,
    actorId: string,
    requirements: {
      complianceFrameworks: string[];
      minSuccessRate: number;
      maxCapacity: number;
      requiredCertifications: string[];
    }
  ): Promise<{
    valid: boolean;
    issues: string[];
    recommendations: string[];
  }> {
    const query = gql`
      query ValidateExpertAssignment($tenantId: String!, $actorId: ID!, $requirements: ExpertRequirementsInput!) {
        validateExpertAssignment(tenantId: $tenantId, actorId: $actorId, requirements: $requirements) {
          valid
          issues
          recommendations
        }
      }
    `;

    return await this.query(query, { tenantId, actorId, requirements });
  }
}